import React, { useState } from "react";
import { FiFile, FiDownload, FiCheck, FiCheckCircle } from "react-icons/fi";
import { Message } from "../../types/conversations";
import { formatTime } from "../../helpers/conversations";
import conversationsService from "../../services/conversations";
import { ImageModal } from "./ImageModal";
import { ExerciseViewModal } from "./ExerciseViewModal";
import { useAuth } from "../../context/AuthContext";

interface MessageItemProps {
  message: Message;
  showDate: boolean;
}

export const MessageItem: React.FC<MessageItemProps> = ({
  message,
  showDate,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExerciseModalOpen, setIsExerciseModalOpen] = useState(false);
  const { getUser } = useAuth();

  // Tenta obter o ID do usuário de múltiplas fontes
  const authUser = getUser();
  const currentUserIdFromService = conversationsService.getCurrentUserId();
  const currentUserId = authUser?.id || currentUserIdFromService;

  // Garantir que ambos são números para comparação correta
  const currentUserIdNum =
    currentUserId !== null && currentUserId !== undefined
      ? Number(currentUserId)
      : null;
  const messageSenderIdNum = Number(message.senderId);

  // Normalizar e validar antes de comparar
  const isFromMe =
    currentUserIdNum !== null &&
    !Number.isNaN(currentUserIdNum) &&
    !Number.isNaN(messageSenderIdNum) &&
    messageSenderIdNum === currentUserIdNum;
 

  const handleImageClick = () => {
    if (message.type === "image" && message.fileUrl) {
      setIsModalOpen(true);
    }
  };

  return (
    <React.Fragment>
      {showDate && (
        <div className="flex justify-center my-3 md:my-4">
          <span className="bg-white dark:bg-gray-800 px-2.5 py-1 rounded-lg text-[11px] text-gray-600 dark:text-gray-400 shadow-sm md:px-3 md:text-xs">
            {(() => {
              const date =
                message.timestamp instanceof Date
                  ? message.timestamp
                  : new Date(message.timestamp);

              if (Number.isNaN(date.getTime())) {
                return "Data inválida";
              }

              return date.toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              });
            })()}
          </span>
        </div>
      )}

      <div className={`flex ${isFromMe ? "justify-end" : "justify-start"}`}>
        <div className={`max-w-[85%] md:max-w-[70%] ${isFromMe ? "order-2" : "order-1"}`}>
          <div
            className={`rounded-lg px-3 py-2 shadow-sm ${
              isFromMe
                ? "bg-purple-400 text-white"
                : "bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            }`}
          >
            {message.type === "text" && (
              <p className="text-[13px] whitespace-pre-wrap break-words md:text-sm">
                {message.content || "(sem conteúdo)"}
              </p>
            )}

            {!message.type && (
              <p className="text-[13px] whitespace-pre-wrap break-words md:text-sm">
                {message.content || "(sem conteúdo)"}
              </p>
            )}

            {message.type === "image" && (
              <div className="space-y-1.5 md:space-y-2">
                <div
                  className="cursor-pointer group relative overflow-hidden rounded-lg active:opacity-80"
                  onClick={handleImageClick}
                >
                  <img
                    src={message.fileUrl}
                    alt="Imagem"
                    className="rounded-lg max-w-full h-auto transition-transform duration-300 group-hover:scale-105"
                  />
                  {/* Overlay com ícone de zoom ao passar o mouse */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/20 backdrop-blur-sm rounded-full p-2.5 md:p-3">
                      <svg
                        className="w-5 h-5 text-white md:w-6 md:h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
                {message.content && (
                  <p className="text-[11px] opacity-90 md:text-xs">{message.content}</p>
                )}
              </div>
            )}

            {message.type === "file" && (
              <div className="flex items-center gap-2.5 bg-white bg-opacity-10 rounded-lg p-2.5 md:gap-3 md:p-3">
                <div className="flex-shrink-0">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center md:w-10 md:h-10 ${
                      isFromMe
                        ? "bg-white bg-opacity-20"
                        : "bg-purple-100 dark:bg-purple-900"
                    }`}
                  >
                    <FiFile
                      size={18}
                      className={`md:text-[20px] ${
                        isFromMe
                          ? "text-white"
                          : "text-purple-600 dark:text-purple-400"
                      }`}
                    />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium truncate md:text-sm">
                    {message.fileName}
                  </p>
                  <p className="text-[11px] opacity-75 md:text-xs">{message.fileSize}</p>
                </div>
                <button className="flex-shrink-0 p-1.5 hover:bg-white hover:bg-opacity-10 rounded-full transition active:scale-95 md:p-1">
                  <FiDownload size={20} className="md:text-[18px]" />
                </button>
              </div>
            )}

            {message.type === "treino" && message.exercise && (
              <div className="space-y-1.5 md:space-y-2">
                <div
                  className={`flex items-center gap-2.5 rounded-lg p-2.5 cursor-pointer transition-colors active:opacity-80 md:gap-3 md:p-3 ${
                    isFromMe
                      ? "bg-white bg-opacity-20 hover:bg-opacity-30"
                      : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                  onClick={() => setIsExerciseModalOpen(true)}
                >
                  {/* Imagem do exercício */}
                  <div className="flex-shrink-0">
                    <img
                      src={
                        message.exercise.image_url ||
                        message.exercise.image ||
                        "/placeholder-exercise.png"
                      }
                      alt={message.exercise.name}
                      className="w-14 h-14 rounded-lg object-cover md:w-16 md:h-16"
                    />
                  </div>
                  {/* Título e descrição */}
                  <div className="flex-1 min-w-0">
                    <h4
                      className={`text-[13px] font-semibold truncate md:text-sm ${
                        isFromMe
                          ? "text-white"
                          : "text-gray-900 dark:text-white"
                      }`}
                    >
                      {message.exercise.name}
                    </h4>
                    {message.exercise.description && (
                      <p
                        className={`text-[11px] italic line-clamp-1 mt-0.5 md:text-xs md:mt-1 ${
                          isFromMe
                            ? "text-white opacity-80"
                            : "text-gray-600 dark:text-gray-300 opacity-70"
                        }`}
                      >
                        {message.exercise.description}
                      </p>
                    )}
                  </div>
                </div>
                {message.content && (
                  <p className="text-[13px] whitespace-pre-wrap break-words md:text-sm">
                    {message.content}
                  </p>
                )}
              </div>
            )}

            <div
              className={`flex items-center gap-1 mt-1 ${
                isFromMe ? "justify-end" : "justify-start"
              }`}
            >
              <span className="text-[10px] opacity-70">
                {formatTime(message.timestamp)}
              </span>
              {isFromMe && (
                <span className="opacity-70">
                  {message.status === "read" ? (
                    <FiCheckCircle size={13} className="text-blue-300 md:text-[14px]" />
                  ) : (
                    <FiCheck size={13} className="md:text-[14px]" />
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de imagem */}
      {message.type === "image" && message.fileUrl && (
        <ImageModal
          imageUrl={message.fileUrl}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          alt="Imagem da conversa"
        />
      )}

      {/* Modal de exercício */}
      {message.type === "treino" && message.exercise && (
        <ExerciseViewModal
          isOpen={isExerciseModalOpen}
          exercise={message.exercise}
          onClose={() => setIsExerciseModalOpen(false)}
        />
      )}
    </React.Fragment>
  );
};
